import React, { useState, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { noop } from 'common/utils';
import { useOutsideClick } from 'hooks/useOutsideClick';
import { Checkbox } from 'components/checkbox/Checkbox';
import { Routes } from 'routes';

export enum TabNames {
    Dashboard = 'personal-dashboard',
    OrderHistory = 'order-history',
    Settings = 'settings',
    CoordinatorDashboard = 'coordinator-dashboard',
}

export interface TabState {
    activeTab: TabNames;
    setActiveTab: (_: TabNames) => void;
}

export interface AccountSettingsState extends TabState {
    pageIsLoading: boolean;
    setPageIsLoading: (_: boolean) => void;
}

const initialState = {
    // page loading controls
    pageIsLoading: true, // initialize to true because we need to fetch org data
    setPageIsLoading: noop, // function to signal the account settings page has loaded all resources

    // tabs
    activeTab: TabNames.Settings, // default our tab to settings
    setActiveTab: noop, // update function to set the active tab
};

export const AccountSettingsContext = React.createContext<AccountSettingsState>(initialState);

export const AccountSettingsView = ({ children }: React.PropsWithChildren) => {
    const [pageIsLoading, setPageIsLoading] = useState<boolean>(true);
    const [activeTab, setActiveTab] = useState<TabNames>(TabNames.Settings);
    const location = useLocation();

    useEffect(() => {
        const { pathname } = location;
        const tab = pathname.split('/');

        // CASE: settings is not a single path but composed of 3 paths
        if (
            pathname === Routes.ShopAccountSettingsPersonalInformationTab ||
            pathname === Routes.ShopAccountSettingsSecurityTab ||
            pathname === Routes.ShopAccountSettingsPaymentOptionsTab ||
            pathname === Routes.ShopAccountSettingsNotificationsTab ||
            pathname === Routes.ShopAccountSettingsInviteFriendsFamilyOptionsTab
        ) {
            setActiveTab(TabNames.Settings);
        } else {
            setActiveTab(tab[tab.length - 1] as TabNames);
        }
    }, [location]);

    const contextValues = {
        pageIsLoading,
        setPageIsLoading,
        activeTab,
        setActiveTab,
    };

    return (
        <AccountSettingsContext.Provider value={contextValues}>
            {children}
        </AccountSettingsContext.Provider>
    );
};

export const DebugView = ({ forceRender }: { forceRender?: boolean }) => {
    const { activeTab, pageIsLoading } = useContext(AccountSettingsContext);
    const [isDebugOpen, setIsDebugOpen] = useState<boolean>(false);
    const [pinned, setPinned] = useState<boolean>(false);
    const ref = useOutsideClick<HTMLDivElement>(() => setIsDebugOpen(false));

    const state = JSON.stringify({ activeTab, pageIsLoading }, null, 2);

    return forceRender ? (
        <div className="fixed top-0 z-10 max-w-lg text-sm">
            <button onClick={() => setIsDebugOpen(true)}>Debug</button>
            {(isDebugOpen || pinned) && (
                <div ref={ref} className="bg-white shadow-lg overflow-x-hidden">
                    <Checkbox
                        checked={pinned}
                        onChange={setPinned}
                        id="pinDebugView"
                        text="Pin Debug View"
                        labelClassName="pl-1"
                    />
                    <pre>
                        <code>{state}</code>
                    </pre>
                </div>
            )}
        </div>
    ) : null;
};
